import { FormEvent, useCallback, useState } from "react"
import { isApiError } from "../../App/ApiError"
import { LanguageTextReplacementFunction } from "../../App/Localization"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { Icon, Icons } from "../../Components/Icon"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { OverlayScrollBody } from "../../Components/OverlayScrollBody"

// The prompt shown when the cloud has stopped accepting this app's session (#1449).
//
// Before this existed a stale session surfaced as an HTTP 500 with a `DecodingError`
// in it: the app fed the cloud's 401 body straight into the decoder for a success
// payload. The user saw an unreadable error, no explanation, and no way to act on it.
//
// Two properties, both @mutle's calls, and both pinned by
// `Tests/PublicTests/Views/App/SessionExpiredView.test.tsx`:
//
//   * Nothing is destroyed. This view never logs anyone out and never clears the
//     local session -- the ship stays open and the trip keeps being logged locally.
//     The active session is replaced only by `onLogin` actually succeeding.
//   * It is dismissable, and an abandoned or failed login leaves the user exactly
//     where they were. That is also why the login form is EMBEDDED here rather than
//     being a link to `/app/login`: reaching that route means clearing the frontend
//     session first, and a user who then backs out would be stranded on a login screen
//     with a session the cloud refuses.

export const SessionExpiredViewData = {
  // Whether a failed request is the app telling us its cloud session died, as opposed
  // to any other 401.
  //
  // Matching on the status alone would be wrong: `PUT /api/ships/:id` answers 401 for
  // "this ship is claimed by another device" (#1453), which a re-login cannot fix and
  // whose real message this prompt would hide. The app raises the distinct
  // `LogbookApiError.sessionExpired` only after verifying with the cloud's
  // `GET /api/session` that the session itself is what died, and that verified verdict
  // -- not the status -- is what this reads.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- error bodies are untyped
  isSessionExpired(error?: any): boolean {
    return isApiError(error, "sessionExpired");
  },
};

interface SessionExpiredViewProps {
  onDismiss: () => void;
  /// Performs the login and returns whether it succeeded. Resolving `false` means the
  /// credentials were refused; rejecting means the attempt could not be made at all.
  onLogin: (email: string, password: string) => Promise<boolean>;
  t: LanguageTextReplacementFunction;
}

export function SessionExpiredView({ onDismiss, onLogin, t }: SessionExpiredViewProps) {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | undefined>(undefined);

  const submit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.target as HTMLFormElement);
    const email = data.get("email") as string;
    const password = data.get("password") as string;
    setLoading(true);
    setLoginError(undefined);
    try {
      const success = await onLogin(email, password);
      if (success) {
        // `app.setSession` repoints the session but leaves the overlay standing, so
        // without this the red alert survives a SUCCESSFUL login and reads exactly like
        // a refusal. Closing on success is this view's job, the mirror of staying open
        // on a failure below.
        onDismiss();
      } else {
        // Deliberately stays open on a failure: closing would drop the user back into
        // an app that still cannot sync, with the prompt gone.
        setLoginError("session_expired_failed");
      }
    } catch {
      setLoginError("session_expired_failed_request");
    }
    setLoading(false);
  }, [onDismiss, onLogin]);

  return (
    <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")}>
      <Block>
        <BlockRow>
          <LocalizedMessage html={true} message={(m) => m.sessionExpired.text} />
        </BlockRow>
        <hr />
        <form className="LoginView-form" onSubmit={submit}>
          {loginError && <div className="LoginView-error">{t(loginError)}</div>}
          <BlockRow>
            <BlockField label={t("session_expired_email")} labelFor="session_expired_email_input">
              <input id="session_expired_email_input" name="email" type="email" autoComplete="username" />
            </BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField label={t("session_expired_password")} labelFor="session_expired_password_input">
              <span className="password-field">
                <input id="session_expired_password_input" name="password" type={showPassword ? "text" : "password"} autoComplete="current-password" />
                <Icon icon={showPassword ? Icons.EyeOn : Icons.EyeOff} className="password-toggle" onClick={() => setShowPassword(!showPassword)} />
              </span>
            </BlockField>
          </BlockRow>
          <BlockRow actions={true} className="block-row-one-row">
            <BlockField>
              <button className="button" type="button" disabled={loading} onClick={onDismiss}>{t("session_expired_dismiss")}</button>
            </BlockField>
            <BlockField>
              <button className="button default" type="submit" disabled={loading}>
                {loading ? t("session_expired_login_loading") : t("session_expired_login")}
              </button>
            </BlockField>
          </BlockRow>
        </form>
      </Block>
    </OverlayScrollBody>
  );
}
